import type { Discount } from "@/stores/bookingStore";

// Personal referral codes shaped like "<firstname>10" (a name + "10", any case)
// give a percentage off. On expensive packages that percentage can dwarf a fair
// referral reward, so the deduction from these codes is capped. The SAME rule is
// enforced server-side in supabase/functions/validate-voucher-v3 — keep them in
// sync.
const CAPPED_REFERRAL_CODE = /^[a-z]+10$/i;
// Max deduction for a capped referral code, in cart-currency minor units
// (150 CHF in CH, 150 EUR in the EUR markets).
export const MAX_REFERRAL_DISCOUNT_CENTS = 15000;

// True for percentage codes matching the "<firstname>10" pattern.
const isCappedReferralCode = (discount: Discount): boolean =>
  discount.type === "percentage" && CAPPED_REFERRAL_CODE.test(discount.code);

/**
 * Voucher discount in cents, computed the way STRIPE computes it.
 *
 * Two rules matter, and getting either wrong charges a customer something
 * other than the total we showed them:
 *
 *  1. NO whole-franc rounding. This used to round the discount to whole francs
 *     so the rendered arithmetic looked tidy, which silently moved the total by
 *     up to 50 cents: a 99.9% code on a 2'409 bundle displayed CHF 2 while
 *     Stripe charged CHF 2.41.
 *  2. A percentage coupon is applied PER INVOICE LINE and rounded per line,
 *     not once on the sum — so pass the lines whenever they are known (the
 *     paths that actually charge). Aggregate and per-line can differ by a cent
 *     per line, and a cent is still a mismatch.
 *
 * `formatPrice` renders centimes when the amount is not a whole franc, so
 * subtotal − discount = total still reads consistently.
 */
export function voucherDiscountCents(
  discount: Discount | null | undefined,
  base: number | number[],
): number {
  if (!discount || discount.value <= 0) return 0;
  const lines = Array.isArray(base) ? base : [base];
  const total = lines.reduce((sum, line) => sum + line, 0);

  let cents =
    discount.type === "percentage"
      ? lines.reduce(
          (sum, line) => sum + Math.round((line * discount.value) / 100),
          0,
        )
      : discount.value;

  cents = Math.min(total, cents);
  // Cap "<firstname>10" percentage codes at the max referral deduction.
  if (isCappedReferralCode(discount)) {
    cents = Math.min(cents, MAX_REFERRAL_DISCOUNT_CENTS);
  }
  return cents;
}
